import { set_button_states } from '@frontend/non_graphical_ui_actions';
import { animate } from '@frontend/scene';
import {
    curvature_comb_scaling_factor,
    curvature_edges,
    curvature_raynode,
    number_of_curvature_combs,
    showing_curvature_combs,
} from './state';
import { isEdge } from '@frontend/lattice';
import {
    uil_get_edge_curvatures,
    uil_get_world_scaling_factor,
    uil_is_object_visible,
} from '@frontend/ui_logic';
import { suppress_animate, unsuppress_animate_if_ready } from '@frontend/utility';
import { filter_ip } from '@vaaas/fpts/array';
import {
    Geometry,
    LineBasicMaterial,
    LineSegments,
    Vector3,
} from 'three';
import { rgbToHex } from '@frontend/colours_materials_appearance';
import { clamp } from '@vaaas/fpts/maths';

export function toggle_edge_curvatures() {
    if (showing_curvature_combs.value) {
        // If curvatures were already visible, we hide them
        hide_edge_curvatures();
        curvature_edges.value = [];
        scene.remove(curvature_raynode);
    } else if (uid_selected_edges.length > 0) {
        // Some edges are selected, so we display curvatures for them
        scene.add(curvature_raynode);
        for (const eid of uid_selected_edges.keys())
            curvature_edges.value.push(eid);
        show_edge_curvatures();
    }
    animate();
    set_button_states();
}

export function hide_edge_curvatures() {
    for (let i = curvature_raynode.children.length - 1; i >= 0; i--) {
        const v = curvature_raynode.children[i]!;
        curvature_raynode.remove(curvature_raynode.children[i]!);
        // @ts-ignore
        v.geometry.dispose();
        // @ts-ignore
        v.material.dispose();
    }
    animate();
    showing_curvature_combs.value = false;
}

const remove_invalid_edges = filter_ip(x => isEdge(x) && uil_is_object_visible(x));

export function show_edge_curvatures() {
    hide_edge_curvatures();

    remove_invalid_edges(curvature_edges.value);

    let edge_count = 0;
    suppress_animate();
    for (const eid of curvature_edges.value) {
        const returned_tuple = uil_get_edge_curvatures(eid, number_of_curvature_combs.value);
        if (!returned_tuple) {
            unsuppress_animate_if_ready();
            return;
        }

        const [sample_coords, sample_vectors] = returned_tuple;

        const mod = edge_count % 3;

        const r = mod === 0 ? 255 : 0;
        const g = mod === 1 ? 255 : 0;
        const b = mod === 2 ? 255 : 0;

        for (let i = 0; i < number_of_curvature_combs.value; i++)
            add_curvature_line_to_scene(sample_coords[i]!, sample_vectors[i]!, r, g, b);

        edge_count++;
    }
    unsuppress_animate_if_ready();
    showing_curvature_combs.value = true;
}

function add_curvature_line_to_scene(v0: Vector3, v1: Vector3, r: number, g: number, b: number) {
    const v = new Vector3()
        .copy(v0)
        .add(v1.multiplyScalar(curvature_comb_scaling_factor.value/uil_get_world_scaling_factor()));

    const e_material = new LineBasicMaterial({
        color: rgbToHex(r, g, b),
        linewidth: 2,
    });
    const e_geometry = new Geometry();
    // e_geometry.dynamic = true;
    e_geometry.vertices = [];
    e_geometry.vertices.push(v0);
    e_geometry.vertices.push(v);
    const e_mesh = new LineSegments(e_geometry, e_material);
    curvature_raynode.add(e_mesh);
    animate();
}

export function set_curvature_comb_scaling_factor(x: number) {
    curvature_comb_scaling_factor.value = clamp(0.001, 100)(x);
    if (showing_curvature_combs.value) {
        toggle_edge_curvatures();
        toggle_edge_curvatures();
    } else {
        toggle_edge_curvatures();
    }
}

export function set_number_of_curvature_combs(n: 20 | 50 | 100 | 200) {
    number_of_curvature_combs.value = n;
    if (showing_curvature_combs.value) {
        toggle_edge_curvatures();
        toggle_edge_curvatures();
    } else {
        toggle_edge_curvatures();
    }
}
